import { useEffect, useState } from 'react';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Modal from '../../ui/Modal';
import Select from '../../ui/Select';
import { ParseDecimalInput, ParseIntegerInput } from '../locationFields';
import { useWarehouseLabels } from '../warehouseLabels';
import { AREA_TYPES, FormatBayCode, ValidateRackPrefix } from './mapModel';

/**
 * Modais de PROPRIEDADES do editor inline, abertos por DUPLO CLIQUE no
 * elemento (interação direta da r3 — criar ÁREA é arrastar o chip da paleta e
 * criar RACK é o traçado de dois cliques + o RackDraftPanel):
 * - AreaEditModal / RackEditModal / BlockEditModal. O bloco edita SÓ o que
 *   o contrato tem (largura e máx. de andares) — andar/posição são dados reais
 *   de wms_locations, nunca duplicados no layout.
 * - Rack e bloco têm a ZONA DESTRUTIVA (excluir) que pede senha e só apaga de
 *   verdade se o backend confirmar vazio; área continua com excluir livre
 *   (desde que sem racks dentro).
 * - NÃO EXISTE MAIS RUA nem "lados" (02/09/2026): os botões de excluir/
 *   adicionar lado ímpar/par morreram junto.
 */

function areaTypeName(label, type) {
    const known = AREA_TYPES[type] ? type : 'outro';
    return label(`wms.map.area_type.${known}`);
}

const InfoBadge = ({ children }) => (
    <span className="inline-block rounded-full bg-mono-100 px-2.5 py-1 text-xs font-semibold text-mono-700 dark:bg-mono-900 dark:text-mono-300">
        {children}
    </span>
);

/** Edição de área: tipo e exclusão (livre — desde que sem racks dentro). */
export const AreaEditModal = ({ isOpen, area, onChangeType, onDelete, onClose }) => {
    const { label } = useWarehouseLabels();

    if (!isOpen || !area) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={label('wms.map.area_title')} size="md">
            <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                    <InfoBadge>{`X1=${area.x1} Y1=${area.y1}`}</InfoBadge>
                    <InfoBadge>{`X2=${area.x2} Y2=${area.y2}`}</InfoBadge>
                </div>
                <Select
                    label={label('wms.map.area_type')}
                    value={AREA_TYPES[area.type] ? area.type : 'outro'}
                    onChange={(event) => onChangeType(event.target.value)}
                >
                    {Object.keys(AREA_TYPES).map((type) => (
                        <option key={type} value={type}>
                            {areaTypeName(label, type)}
                        </option>
                    ))}
                </Select>
                <div className="flex flex-wrap gap-3">
                    <Button type="button" variant="danger" onClick={onDelete}>
                        {label('wms.map.delete_area')}
                    </Button>
                </div>
                <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.map.area_edit_note')}</p>
            </div>
        </Modal>
    );
};

/**
 * Edição de rack: nome + prefixo real; info; e a ZONA DESTRUTIVA do editor —
 * excluir o rack inteiro. A exclusão pede senha (o WarehouseMap abre o
 * ConfirmPasswordModal) e só acontece de verdade se o backend confirmar (rack
 * vazio); o desenho só some depois.
 */
export const RackEditModal = ({
    isOpen,
    rack,
    model,
    blocksCount,
    containerLabel,
    busy = false,
    onSave,
    onDeleteRack,
    onClose,
}) => {
    const { label } = useWarehouseLabels();
    const [name, setName] = useState('');
    const [prefixValue, setPrefixValue] = useState('');
    const [fieldError, setFieldError] = useState(null);

    useEffect(() => {
        if (!isOpen || !rack) {
            return;
        }
        setName(rack.name || '');
        setPrefixValue(rack.rack || '');
        setFieldError(null);
    }, [isOpen, rack]);

    if (!isOpen || !rack) {
        return null;
    }

    const save = () => {
        const prefixError = ValidateRackPrefix(model, prefixValue, rack.id);
        if (prefixError) {
            setFieldError(prefixError);
            return;
        }
        onSave({ name: name.trim(), rack: prefixValue.trim().toUpperCase() });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={label('wms.map.rack_title')} size="lg">
            <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Input
                        label={label('wms.map.rack_name')}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder={`Rack ${prefixValue || ''}`.trim()}
                        autoComplete="off"
                    />
                    <Input
                        label={label('wms.map.rack_prefix')}
                        value={prefixValue}
                        maxLength={2}
                        onChange={(event) => setPrefixValue(event.target.value.toUpperCase())}
                        errorMessage={fieldError || undefined}
                        autoComplete="off"
                    />
                </div>
                <div className="space-y-1 text-sm text-mono-600 dark:text-mono-400">
                    <p>
                        {label('wms.map.orientation_label')}:{' '}
                        {label(
                            rack.orientation === 'vertical'
                                ? 'wms.map.orientation_vertical'
                                : 'wms.map.orientation_horizontal',
                        )}
                    </p>
                    <p>
                        {label('wms.map.bay_side')}: {label(`wms.map.side.${rack.baySide}`)}
                    </p>
                    <p>
                        {label('wms.map.blocks_count')}: {blocksCount}
                    </p>
                    <p>
                        {label('wms.map.container_area')}: {containerLabel || '—'}
                    </p>
                </div>
                <div className="flex flex-wrap gap-3">
                    <Button type="button" onClick={save} disabled={busy}>
                        {label('wms.common.save')}
                    </Button>
                </div>
                <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.map.rack_edit_note')}</p>

                {/* Zona destrutiva do editor: excluir o RACK inteiro. Pede senha (o
                    WarehouseMap abre o ConfirmPasswordModal) e só apaga de verdade
                    se o backend confirmar vazio; o desenho só some depois. */}
                {onDeleteRack ? (
                    <div className="border-t border-mono-200 pt-3 dark:border-mono-800">
                        <Button type="button" variant="danger" size="sm" onClick={onDeleteRack} disabled={busy}>
                            {label('wms.map.delete_rack')}
                        </Button>
                        <p className="mt-1.5 text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.map.delete_rack_hint')}
                        </p>
                    </div>
                ) : null}
            </div>
        </Modal>
    );
};

/**
 * Edição de bloco: largura do vão (m) e máximo de andares.
 *
 * ⚠️ Os dois campos NÃO gravam no mesmo lugar, e a tela diz isso: a largura vai
 * para o CADASTRO do bloco (`wms_blocks.bay_width`, via edição em massa de um
 * bloco só) **e** para o desenho; o máximo de andares é só do desenho. Como o
 * dono definiu (M4-4) que largura diferente se ajusta DEPOIS do rack gerado, é
 * aqui que ele clica, e é aqui que tem de valer.
 */
export const BlockEditModal = ({ isOpen, block, rack, onSave, onDeleteBlock, onClose, saving = false, busy = false }) => {
    const { label } = useWarehouseLabels();
    const [widthValue, setWidthValue] = useState('');
    const [maxLevelsValue, setMaxLevelsValue] = useState('');
    const [fieldError, setFieldError] = useState(null);

    useEffect(() => {
        if (!isOpen || !block) {
            return;
        }
        setWidthValue(block.width === null || block.width === undefined ? '' : String(block.width));
        setMaxLevelsValue(block.maxLevels === null || block.maxLevels === undefined ? '' : String(block.maxLevels));
        setFieldError(null);
    }, [isOpen, block]);

    if (!isOpen || !block) {
        return null;
    }

    const save = () => {
        const width = ParseDecimalInput(widthValue);
        if (!Number.isFinite(width) || width <= 0) {
            setFieldError(`${label('wms.map.block_width')}: informe um valor maior que zero.`);
            return;
        }
        const maxLevels = ParseIntegerInput(maxLevelsValue);
        if (!Number.isFinite(maxLevels) || maxLevels < 1) {
            setFieldError(`${label('wms.map.max_levels')}: informe um inteiro maior ou igual a 1.`);
            return;
        }
        setFieldError(null);
        onSave({ width, maxLevels });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={label('wms.map.block_title')} size="md">
            <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                    <InfoBadge>{rack ? rack.name || `Rack ${rack.rack}` : label('wms.map.rack_title')}</InfoBadge>
                    <InfoBadge>{rack ? FormatBayCode(rack.rack, block.bay) : `${label('wms.field.bay')} ${block.bay}`}</InfoBadge>
                    <InfoBadge>{`X=${block.x} Y=${block.y}`}</InfoBadge>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Input
                        label={label('wms.map.block_width')}
                        inputMode="decimal"
                        value={widthValue}
                        onChange={(event) => setWidthValue(event.target.value)}
                        autoComplete="off"
                    />
                    <Input
                        label={label('wms.map.max_levels')}
                        type="number"
                        min={1}
                        value={maxLevelsValue}
                        onChange={(event) => setMaxLevelsValue(event.target.value)}
                    />
                </div>
                {/* O que cada campo grava — a explicação fica onde o clique
                    acontece, não numa nota de rodapé de outra tela. */}
                <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.map.block_width_note')}</p>
                {fieldError ? (
                    <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                        {fieldError}
                    </p>
                ) : null}
                <Button type="button" onClick={save} loading={saving} disabled={busy} className="w-full">
                    {label('wms.common.save')}
                </Button>

                {/* Zona destrutiva: excluir SÓ este bloco (vira um gap no rack).
                    Pede senha e só apaga se estiver vazio — a trava é no backend. */}
                {onDeleteBlock ? (
                    <div className="border-t border-mono-200 pt-3 dark:border-mono-800">
                        <Button type="button" variant="danger" size="sm" onClick={onDeleteBlock} disabled={busy || saving}>
                            {label('wms.map.delete_block')}
                        </Button>
                        <p className="mt-1.5 text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.map.delete_block_hint')}
                        </p>
                    </div>
                ) : null}
            </div>
        </Modal>
    );
};

/**
 * Confirmação de ADICIONAR um bloco individual num GAP do corredor: o dono
 * clicou numa célula vazia onde caberia o Bloco N (o WarehouseMap detecta a
 * posição via FindAddableBlockAt e passa o alvo `{rackId, rack, bay}` aqui).
 * Confirmar → o mapa abre o ConfirmPasswordModal → gera o bloco. `busy` trava
 * enquanto a geração roda.
 */
export const BlockAddModal = ({ isOpen, target, rackName, busy = false, onConfirm, onClose }) => {
    const { label } = useWarehouseLabels();
    if (!isOpen || !target) {
        return null;
    }
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={label('wms.map.add_block_title')} size="sm">
            <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                    <InfoBadge>{rackName}</InfoBadge>
                    <InfoBadge>{FormatBayCode(target.rack, target.bay)}</InfoBadge>
                </div>
                <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.map.add_block_hint')}</p>
                <div className="flex flex-col gap-2 sm:flex-row">
                    <Button type="button" onClick={onConfirm} loading={busy} className="flex-1">
                        {`${label('wms.map.add_block')} ${target.bay}`}
                    </Button>
                    <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
                        {label('wms.common.cancel')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
